import React from 'react';
import useRequireAuth from '../../hooks/useRequireAuth';
import { formatDateForDisplay } from '../../utils/dateFormatter';
import { getDocumentTypeText } from '../../utils/documentTypeUtils';

// 代理店編集確認ポップアップコンポーネント
export const AgencyEditConfirmationPop = ({
  showConfirmationPopup,
  formData,
  closeConfirmationPopup,
  confirmEdit,
  activeTab,
  selectedCompanies = {},
  insuranceCompanies = {},
  documentTypes = []
}) => {
  const userInfo = useRequireAuth();
  const canEditDocumentTypes = userInfo?.is_admin;

  if (!showConfirmationPopup) return null;

  // 選択された保険会社のリストを取得
  const getSelectedInsuranceCompanies = () => {
    const selectedList = {};
    Object.keys(insuranceCompanies).forEach(type => {
      Object.entries(insuranceCompanies[type]).forEach(([category, companies]) => {
        if (!selectedList[type]) selectedList[type] = [];
        companies.forEach(company => {
          if (selectedCompanies.includes(company.id)) {
            selectedList[type].push(company);
          }
        });
      });
    });
    return selectedList;
  };

  // コンポーネントの先頭付近で関数を定義
  const getCommentText = () => {
    if (activeTab === 'solicitors') {
      return '上記の内容で募集人情報を更新します。変更を保存してよいですか？';
    }
    // それ以外（代理店タブ info / insurance）は同じメッセージにする
    return '上記の内容で代理店情報を更新します。変更を保存してよいですか？';
  };

  // アクティブなタブに基づいて表示するコンテンツを決定
  const renderContent = () => {
    switch (activeTab) {
      case 'info': {
        const documentTypeText = canEditDocumentTypes
          ? getDocumentTypeText(formData.documentTypeIds, documentTypes)
          : '';

        return (
          <>
            <div className="confirmation-files">
              <div className="confirmation-file-item font-large">
                <div className="file-path">取引先コード: {formData.tradingCode}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">アカウント名（メールアドレス）: {formData.accountname}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">代理店名: {formData.name}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">代理店カナ: {formData.nameKana}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">契約日: {formatDateForDisplay(formData.contractDate)}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">解除日/契約終了日: {formatDateForDisplay(formData.cancellationDate)}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">郵便番号: {formData.postalCode}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">住所: {formData.prefecture}{formData.city}{formData.building}</div>
              </div>
              <div className="confirmation-file-item font-large">
                <div className="file-path">電話番号: {formData.phoneNumber}</div>
              </div>
              {canEditDocumentTypes && (
                <div className="confirmation-file-item font-large">
                  <div className="file-path">文書種別: {documentTypeText}</div>
                </div>
              )}
            </div>
          </>
        );
      }
      case 'insurance':
        const selectedInsuranceList = getSelectedInsuranceCompanies();
        return (
          <div className="confirmation-files">
            {Object.keys(selectedInsuranceList).map(type => (
              <div key={type}>
                <div className="confirmation-file-item font-large">
                  <div className="file-path">選択された{type} ({selectedInsuranceList[type].length}社)</div>
                </div>
                {selectedInsuranceList[type].length > 0 ? (
                  <div className="selected-insurance-list" style={{ marginTop: '10px', marginLeft: '20px' }}>
                    {selectedInsuranceList[type].map((company, index) => (
                      <div key={company.id} className="confirmation-file-item font-medium">
                        <div className="file-path">・{company.name}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="confirmation-file-item font-medium" style={{ marginLeft: '20px' }}>
                    <div className="file-path">選択された{type}はありません</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      case 'solicitors':
        return (
          <div className="confirmation-files">
            <div className="confirmation-file-item font-large">
              <div className="file-path">支店名: {formData.branchName}</div>
            </div>
            <div className="confirmation-file-item font-large">
              <div className="file-path">アカウント名（メールアドレス）: {formData.accountname}</div>
            </div>
            <div className="confirmation-file-item font-large">
              <div className="file-path">担当者名漢字: {formData.name}</div>
            </div>
            <div className="confirmation-file-item font-large">
              <div className="file-path">担当者カナ: {formData.nameKana}</div>
            </div>
            {!userInfo.is_agent || userInfo.is_admin && (
              <div className="confirmation-file-item font-large">
                <div className="file-path">
                  利用期間: {formatDateForDisplay(formData.startDate)} 〜 {formatDateForDisplay(formData.endDate)}
                </div>
              </div>
            )}
            <div className="confirmation-file-item font-large">
              <div className="file-path">プロフィール: {formData.profile}</div>
            </div>
            <div className="confirmation-file-item font-large">
              <div className="file-path">利用状況: {formData.isActive ? '利用中' : '停止中'}</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // タブに応じたタイトルの決定
  const getTitle = () => {
    switch (activeTab) {
      case 'info': return '代理店情報編集確認';
      case 'insurance': return '登録保険会社申請確認';
      case 'solicitors': return '募集人編集確認';
      default: return '編集確認';
    }
  };

  // タブに応じた確定ボタンのテキストの決定
  const getConfirmButtonText = () => {
    switch (activeTab) {
      case 'insurance': return '申請する';
      default: return '確定する';
    }
  };

  return (
    <div className="confirmation-popup-overlay">
      <div className="confirmation-popup-container">
        <div className="confirmation-popup-content">
          <h2 className="confirmation-title">{getTitle()}</h2>

          <div className="confirmation-details-02">
            {renderContent()}

            <div className="confirmation-comment">
              <div className="comment-header-error">注意事項</div>
              <div className="comment-content comment-content--error">
                {getCommentText()}
              </div>
            </div>
          </div>

          <div className="action-buttons c-max-mini c-m-center">
            <button
              className="button button-secondary button-medium"
              onClick={closeConfirmationPopup}
            >
              キャンセル
            </button>
            <button
              className="btn btn-primary button-medium"
              onClick={confirmEdit}
            >
              {getConfirmButtonText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyEditConfirmationPop;
